import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  backgroundColor,
  border,
  borderColor,
  borderRadius,
  borderStyle,
  borderWidth,
  position,
  size,
  themeGet,
  zIndex,
} from 'styled-system';

import { overflowY } from '../../../theme/system-utilities';
import type { WithTheme } from '../../../theme/theme';
import { Flex } from '../../Flex';

interface ProgressProps {
  isFirst: boolean;
  isLast: boolean;
  isActive: boolean;
  isCompleted: boolean;
  isNextCompleted: boolean;
}

const CIRCLE_SIZE_S = 8;
const CIRCLE_SIZE_L = 12;
const CIRCLE_GRAY = 'sys.neutral.border.weak';
const CIRCLE_ORANGE = 'sys.primary.background.default';
const CIRCLE_ACTIVE_BORDER_SIZE = 2;
const PROGRESS_BAR_WIDTH = 2;
const PROGRESS_BASIS = (CIRCLE_SIZE_L + PROGRESS_BAR_WIDTH / 2) * 2 + PROGRESS_BAR_WIDTH;

/**
 * Multiplying by 2 required because of the
 * hollow wrapper that wraps a circle
 */
const getCircleHeight = (isLarge: boolean) => (isLarge ? CIRCLE_SIZE_L : CIRCLE_SIZE_S) * 2;

const calcNotLastItemHeightValue = (isFirst: boolean, isActive: boolean) =>
  isFirst ? '100%' : `calc(100% - ${getCircleHeight(isActive)}px)`;

const calcNotLastItemBottomValue = (isFirst: boolean, isActive: boolean) =>
  isFirst ? '-50%' : `calc(-50% + ${getCircleHeight(isActive)}px)`;

const progressLinePseudoBaseCSS = css`
  content: ' ';
  position: absolute;
  left: ${CIRCLE_SIZE_L + PROGRESS_BAR_WIDTH / 2}px;
  border-left: ${PROGRESS_BAR_WIDTH}px solid;
`;

const progressLineNotFirstCSS = ({ isActive, isCompleted, theme }: WithTheme<ProgressProps>) => css`
  padding-top: ${themeGet('space.sp_20')({ theme })}px;

  &::before {
    top: 0;
    height: calc(50% + ${CIRCLE_SIZE_S / 2}px);
    border-color: ${themeGet(`colors.${isActive || isCompleted ? CIRCLE_ORANGE : CIRCLE_GRAY}`)({
      theme,
    })};
  }
`;

const progressLineNotLastCSS = ({
  isFirst,
  isActive,
  isCompleted,
  isNextCompleted,
  theme,
}: WithTheme<ProgressProps>) => css`
  &::after {
    bottom: ${calcNotLastItemBottomValue(isFirst, isActive)};
    height: ${calcNotLastItemHeightValue(isFirst, isActive)};
    border-color: ${themeGet(
      `colors.${
        ((isNextCompleted || isCompleted) && (isActive || isCompleted)) || (isFirst && !isActive)
          ? CIRCLE_ORANGE
          : CIRCLE_GRAY
      }`
    )({ theme })};
  }
`;

const progressLineCSS = (props: WithTheme<ProgressProps>) => css`
  &::before,
  &::after {
    ${progressLinePseudoBaseCSS};
  }
  ${!props.isFirst && progressLineNotFirstCSS(props)}
  ${!props.isLast && progressLineNotLastCSS(props)}
`;

const StyledProgress = styled(Flex)`
  ${position};
  ${overflowY};
  ${progressLineCSS}
`;

const styledProgressStyles = {
  as: 'span',
  alignItems: 'center',
  flexBasis: PROGRESS_BASIS,
  justifyContent: 'center',
  position: 'relative',
  overflowY: 'hidden',
};

const Circle = styled(Flex)`
  ${backgroundColor};
  ${borderStyle};
  ${borderColor};
  ${border};
  ${borderRadius};
  ${borderWidth};
  ${zIndex};
`;

const circleStyles = {
  as: 'span',
  backgroundColor: 'sys.neutral.background.default',
  borderRadius: 'circle',
  borderWidth: `${CIRCLE_ACTIVE_BORDER_SIZE}px`,
  zIndex: 1,
};

const GrayCircle = styled(Circle)<any>`
  ${size};
`;

const grayCircleStyles = {
  ...circleStyles,
  borderStyle: 'solid',
  borderWidth: 'small',
  borderColor: 'sys.neutral.border.inactive',
  size: CIRCLE_SIZE_S,
};

const OrangeCircleWrapper = styled(Circle)<any>`
  ${size};
`;

const orangeCircleWrapperStyles = {
  ...circleStyles,
  alignItems: 'center',
  borderStyle: 'solid',
  borderWidth: 'small',
  borderColor: 'sys.primary.border.default',
  justifyContent: 'center',
  size: CIRCLE_SIZE_L * 2,
};

const OrangeCircle = styled(Circle)<any>`
  ${backgroundColor};
  ${borderRadius};
  ${size};
`;

const orangeCircleStyles = {
  ...circleStyles,
  backgroundColor: CIRCLE_ORANGE,
  borderWidth: 0,
  size: CIRCLE_SIZE_L,
};

const Progress = ({ isFirst, isLast, isActive, isCompleted, isNextCompleted }: ProgressProps) => {
  const isLastCompleted = isCompleted && isLast;

  return (
    <StyledProgress
      isFirst={isFirst}
      isLast={isLast}
      isActive={isActive}
      isCompleted={isCompleted}
      isNextCompleted={isNextCompleted}
      {...styledProgressStyles}
    >
      {isCompleted || isLastCompleted ? (
        <OrangeCircle {...orangeCircleStyles} />
      ) : isActive ? (
        <OrangeCircleWrapper {...orangeCircleWrapperStyles}>
          <OrangeCircle {...orangeCircleStyles} />
        </OrangeCircleWrapper>
      ) : (
        <GrayCircle {...grayCircleStyles} />
      )}
    </StyledProgress>
  );
};

export default Progress;
